import { useEffect, useMemo, useRef, useState } from 'react';
import { ChemError, GASES, GAS_LIMITS, celsius, idealGas, prettyFormula, volumeAtPressure } from '@chemai/engine';
import { type MessageKey, useI18n } from '../../i18n';
import { NumberField, SaveButton, Segmented, SelectField, SimBadge, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';

type Keep = 'volume' | 'pressure';
type Changed = 'T' | 'V' | 'n' | null;

interface Dot { x: number; y: number; vx: number; vy: number }

function GasBox({ V, vrms, count }: { V: number; vrms: number; count: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dots = useRef<Dot[]>([]);
  const state = useRef({ V, vrms });
  state.current = { V, vrms };

  // keep the particle count in step with n
  useEffect(() => {
    const d = dots.current;
    while (d.length < count) {
      const a = Math.random() * Math.PI * 2;
      d.push({ x: Math.random(), y: Math.random(), vx: Math.cos(a) * (0.6 + Math.random() * 0.8), vy: Math.sin(a) * (0.6 + Math.random() * 0.8) });
    }
    d.length = count;
  }, [count]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let last = performance.now();
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = c.clientWidth;
      const h = c.clientHeight;
      if (c.width !== Math.round(w * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const boxW = 40 + (w - 70) * (state.current.V / GAS_LIMITS.V.max);
      const boxH = h - 20;
      const speed = (state.current.vrms / 517) * 140; // px/s, 140 px/s for N2 at 300 K
      // container
      ctx.strokeStyle = getComputedStyle(c).getPropertyValue('--ink').trim() || '#18202c';
      ctx.lineWidth = 3;
      ctx.strokeRect(10, 10, boxW, boxH);
      // piston
      ctx.fillStyle = '#a85a06';
      ctx.fillRect(10 + boxW - 4, 10, 8, boxH);
      ctx.fillRect(10 + boxW + 4, 10 + boxH / 2 - 3, 22, 6);
      ctx.fillStyle = 'rgba(13, 112, 104, 0.85)';
      for (const d of dots.current) {
        if (!reduce) {
          d.x += (d.vx * speed * dt) / boxW;
          d.y += (d.vy * speed * dt) / boxH;
          if (d.x < 0) { d.x = -d.x; d.vx = Math.abs(d.vx); }
          if (d.x > 1) { d.x = 2 - d.x; d.vx = -Math.abs(d.vx); }
          if (d.y < 0) { d.y = -d.y; d.vy = Math.abs(d.vy); }
          if (d.y > 1) { d.y = 2 - d.y; d.vy = -Math.abs(d.vy); }
        }
        d.x = Math.min(1, Math.max(0, d.x));
        ctx.beginPath();
        ctx.arc(14 + d.x * (boxW - 12), 14 + d.y * (boxH - 8), 3.2, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={ref} className="gas-canvas" aria-hidden="true" />;
}

function PvGraph({ n, T, V, P }: { n: number; T: number; V: number; P: number }) {
  const { t, num } = useI18n();
  const W = 520, H = 260, m = { l: 56, r: 12, t: 12, b: 40 };
  const pmax = Math.max(P * 1.6, (n * 8.314 * T) / 10);
  const x = (v: number) => m.l + ((v - GAS_LIMITS.V.min) / (GAS_LIMITS.V.max - GAS_LIMITS.V.min)) * (W - m.l - m.r);
  const y = (p: number) => m.t + (1 - Math.min(p, pmax) / pmax) * (H - m.t - m.b);
  const pts: string[] = [];
  for (let v = GAS_LIMITS.V.min; v <= GAS_LIMITS.V.max; v += 0.25) pts.push(`${pts.length ? 'L' : 'M'}${x(v).toFixed(1)},${y((n * 8.314462618 * T) / v).toFixed(1)}`);
  return (
    <figure className="chart">
      <figcaption className="chart-title">{t('gas.graph')}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${t('gas.graph')}: V = ${num(V)} L, P = ${num(P, 1)} kPa`}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={m.l} x2={W - m.r} y1={y(pmax * f)} y2={y(pmax * f)} className="grid" />
            <text x={m.l - 6} y={y(pmax * f) + 4} className="tick" textAnchor="end">{num(pmax * f, 0)}</text>
          </g>
        ))}
        {[1, 10, 20, 30, 40, 50].map((v) => <text key={v} x={x(v)} y={H - m.b + 18} className="tick" textAnchor="middle">{v}</text>)}
        <text x={(W + m.l) / 2} y={H - 4} className="axis-label" textAnchor="middle">{t('gas.axisV')}</text>
        <text x={12} y={(H - m.b) / 2} className="axis-label" textAnchor="middle" transform={`rotate(-90 12 ${(H - m.b) / 2})`}>{t('gas.axisP')}</text>
        <path d={pts.join('')} className="curve-measured" />
        <circle cx={x(V)} cy={y(P)} r={6} className="pt-current" />
      </svg>
    </figure>
  );
}

export function GasSim() {
  const { t, num, errorMessage } = useI18n();
  const [gasId, setGasId] = useState('N2');
  const [n, setN] = useState(1);
  const [T, setT] = useState(300);
  const [V, setV] = useState(24);
  const [keep, setKeep] = useState<Keep>('volume');
  const [pTarget, setPTarget] = useState<number | null>(null);
  const [changed, setChanged] = useState<Changed>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const M = GASES[gasId].molarMass;
  const state = useMemo(() => idealGas(n, T, V, M), [n, T, V, M]);

  const switchKeep = (k: Keep) => { setKeep(k); setPTarget(k === 'pressure' ? state.P : null); setWarning(null); };

  /** In constant-pressure mode the piston moves: V follows from n and T. */
  const apply = (nextN: number, nextT: number, what: Changed) => {
    setWarning(null);
    if (keep === 'pressure' && pTarget) {
      const v = volumeAtPressure(nextN, nextT, pTarget);
      if (v < GAS_LIMITS.V.min || v > GAS_LIMITS.V.max) {
        setWarning(errorMessage(new ChemError('OUT_OF_RANGE', 'volume out of range', `V = ${num(v, 1)} L`)));
        return;
      }
      setV(v);
    }
    setN(nextN);
    setT(nextT);
    setChanged(what);
  };

  const law: MessageKey | null =
    changed === 'V' ? 'gas.law.boyle' : changed === 'T' ? (keep === 'pressure' ? 'gas.law.charles' : 'gas.law.gayLussac') : null;

  return (
    <div className="sim">
      <SimBadge />
      <div className="sim-grid">
        <section className="panel">
          <h3>{t('tit.setup')}</h3>
          <SelectField label={t('gas.gas')} value={gasId} onChange={setGasId}
            options={Object.keys(GASES).map((g) => ({ value: g, label: prettyFormula(GASES[g].formula) }))} />
          <Segmented<Keep> label={t('gas.keep')} value={keep} onChange={switchKeep}
            options={[{ value: 'volume', label: t('gas.keep.volume') }, { value: 'pressure', label: t('gas.keep.pressure') }]} />
          <NumberField label={t('gas.T')} value={T} onChange={(v) => apply(n, v, 'T')} min={GAS_LIMITS.T.min} max={GAS_LIMITS.T.max} step={5} slider />
          <NumberField label={t('gas.V')} value={V} onChange={(v) => { setV(v); setChanged('V'); setWarning(null); }} min={GAS_LIMITS.V.min} max={GAS_LIMITS.V.max} step={0.5} slider disabled={keep === 'pressure'} />
          <NumberField label={t('gas.n')} value={n} onChange={(v) => apply(v, T, 'n')} min={GAS_LIMITS.n.min} max={GAS_LIMITS.n.max} step={0.05} slider />
          {warning && <p className="warn" role="alert">{warning}</p>}
        </section>

        <section className="panel apparatus-panel">
          <h3>{t('lab.equipment')}</h3>
          <GasBox V={V} vrms={state.vrms} count={Math.max(3, Math.min(150, Math.round(n * 30)))} />
          <p className="muted small">{t('gas.particlesNote')}</p>
        </section>

        <section className="panel readouts" aria-live="polite">
          <h3>{t('lab.observations')}</h3>
          <div className="stats">
            <Stat label={t('gas.P')} value={num(state.P, 1)} unit="kPa" emphasis />
            <Stat label={t('gas.P')} value={num(state.Patm, 3)} unit="atm" />
            <Stat label={t('gas.T')} value={t('gas.celsius', { c: num(celsius(T), 1) })} />
            <Stat label={t('gas.vrms')} value={num(state.vrms, 0)} unit="m/s" />
          </div>
          <ul className="calc">
            <li><code>P = nRT / V = {num(n, 2)} · 8.314 · {num(T, 0)} / {num(V, 2)} = {num(state.P, 1)} kPa</code></li>
            <li><code>v = √(3RT/M) = √(3 · 8.314 · {num(T, 0)} / {num(M / 1000, 4)}) = {num(state.vrms, 0)} m/s</code></li>
          </ul>
          {law && <p className="hint">{t(law)}</p>}
          <SaveButton onSave={() => progress.recordExperiment('gas', { n, T, V: Math.round(V * 100) / 100, P: Math.round(state.P * 10) / 10 })} />
        </section>
      </div>
      <section className="panel">
        <PvGraph n={n} T={T} V={V} P={state.P} />
        <p className="muted small">{t('gas.idealNote')}</p>
      </section>
    </div>
  );
}
